import { AUTH_CONFIG, DEV_USER } from '../../config/config';

/**
 * Servicio de autenticación
 * 
 * Encapsula la lógica de autenticación para que el resto de la aplicación
 * no necesite conocer si está usando DEV_API_KEY o Auth0.
 * 
 * Modo dev: usa DEV_API_KEY directamente
 * Modo auth0: delega a useAuth hook (que usa Auth0 SDK)
 */
class AuthService {
  constructor() {
    this._mode = AUTH_CONFIG.MODE;
    this._devApiKey = AUTH_CONFIG.DEV.API_KEY;
    this._devUser = DEV_USER;
    this._auth0LoginWithRedirect = null;
  }

  /**
   * Registra la función de login de Auth0 (llamado desde useAuth)
   */
  setAuth0Login(fn) {
    this._auth0LoginWithRedirect = fn;
  }

  /**
   * Retorna el modo de autenticación actual
   */
  getMode() {
    return this._mode;
  }

  /**
   * Verifica si está en modo desarrollo
   */
  isDevMode() {
    return this._mode === 'dev';
  }

  /**
   * Inicia el proceso de login
   * 
   * En modo dev: marca la sesión como autenticada con el usuario DEV
   * En modo auth0: delega a Auth0 loginWithRedirect
   */
  login() {
    if (this.isDevMode()) {
      // En modo dev, guardar usuario DEV en sessionStorage
      sessionStorage.setItem('dev_authenticated', 'true');
      sessionStorage.setItem('token', this._devApiKey);
      sessionStorage.setItem('user', JSON.stringify(this._devUser));
      // Recargar para que App detecte el usuario
      window.location.reload();
    } else {
      // En modo auth0, delegar a Auth0
      if (this._auth0LoginWithRedirect) {
        this._auth0LoginWithRedirect();
      } else {
        console.error('[AuthService] Auth0 login not configured');
      }
    }
  }

  /**
   * Obtiene el token/credencial de autenticación
   * 
   * En modo dev: retorna DEV_API_KEY
   * En modo auth0: retorna el token desde sessionStorage (que useAuth guarda)
   */
  getToken() {
    if (this.isDevMode()) {
      return Promise.resolve(this._devApiKey);
    }
    
    // En modo auth0, el token se obtiene de sessionStorage
    // donde useAuth() lo guarda automáticamente
    const token = sessionStorage.getItem('token');
    return Promise.resolve(token || '');
  }

  /**
   * Obtiene información del usuario actual
   * 
   * En modo dev: retorna el usuario de desarrollo
   * En modo auth0: retorna el usuario desde sessionStorage
   */
  getUser() {
    if (this.isDevMode()) {
      return Promise.resolve(this._devUser);
    }
    
    // En modo auth0, useAuth() guarda el usuario en sessionStorage
    const userStr = sessionStorage.getItem('user');
    if (userStr) {
      return Promise.resolve(JSON.parse(userStr));
    }
    return Promise.resolve(null);
  }

  /**
   * Verifica si el usuario está autenticado
   * 
   * En modo dev: verifica si hay sesión DEV activa
   * En modo auth0: verifica si hay token en sessionStorage
   */
  isAuthenticated() {
    if (this.isDevMode()) {
      return sessionStorage.getItem('dev_authenticated') === 'true';
    }
    
    return !!sessionStorage.getItem('token');
  }

  /**
   * Limpia la sesión (logout)
   * 
   * En modo dev: limpia sessionStorage
   * En modo auth0: limpia sessionStorage (el componente useAuth llamará logout de Auth0)
   */
  clearSession() {
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('dev_authenticated');
  }
}

// Exportar instancia única (singleton)
export const authService = new AuthService();

// Exportar clase para testing
export default AuthService;
